import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { formatEuro } from '../lib/calcoli'

const numero = value => {
  const parsed = parseFloat(String(value ?? '').replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

export default function CalcolatoreFinanziamento() {
  const { user } = useAuth()
  const [pratiche, setPratiche] = useState([])
  const [loading, setLoading] = useState(true)
  const [errore, setErrore] = useState('')
  const [praticaId, setPraticaId] = useState('')
  const [importoFinanziato, setImportoFinanziato] = useState('')
  const [costiFissi, setCostiFissi] = useState('0')
  const [costiExtraMensili, setCostiExtraMensili] = useState('0')
  const [durataMesi, setDurataMesi] = useState('60')
  const [tan, setTan] = useState('')
  const [taeg, setTaeg] = useState('')

  useEffect(() => {
    let attivo = true
    async function carica() {
      setLoading(true)
      const { data, error } = await supabase
        .from('pratiche')
        .select('id, numero_pratica, cliente_nome, cliente_cognome, tipo_operazione, dati_comuni, scelta_compagnia, totale_finale, stato, updated_at')
        .eq('dealer_id', user.id)
        .eq('stato', 'in_processamento')
        .order('updated_at', { ascending: false })
      if (!attivo) return
      if (error) setErrore('Non è stato possibile caricare le polizze attive.')
      else setPratiche(data || [])
      setLoading(false)
    }
    if (user?.id) carica()
    return () => { attivo = false }
  }, [user?.id])

  const pratica = useMemo(() => pratiche.find(p => p.id === praticaId) || null, [pratiche, praticaId])
  const dc = pratica?.dati_comuni || {}
  const premioCvt = numero(pratica?.preventivi?.[pratica?.scelta_compagnia]?.totale_assicurazione
    ?? pratica?.preventivi?.[pratica?.scelta_compagnia]?.totale
    ?? pratica?.totale_finale)
  const prezzoAuto = numero(dc.valore_auto)
  const durata = Math.floor(numero(durataMesi))
  const capitale = numero(importoFinanziato) + numero(costiFissi)
  const tanAnnuale = numero(tan)
  const rata = durata > 0 && capitale >= 0
    ? tanAnnuale === 0
      ? capitale / durata
      : capitale * (tanAnnuale / 1200) / (1 - Math.pow(1 + tanAnnuale / 1200, -durata))
    : null
  const totaleMensile = rata == null ? null : rata + numero(costiExtraMensili)
  const totaleRimborsato = rata == null ? null : rata * durata
  const interessiStimati = totaleRimborsato == null ? null : totaleRimborsato - capitale
  const totalePagamenti = totaleMensile == null ? null : totaleMensile * durata
  const importoRiferimento = prezzoAuto + premioCvt

  return (
    <div className="page finance-page">
      <div className="page-header">
        <div>
          <h1>Calcolatore finanziamento</h1>
          <p>Imposta i dati del finanziamento e ottieni una stima immediata della rata.</p>
        </div>
      </div>

      {errore && <div className="alert alert-error">{errore}</div>}

      <section className="finance-section">
        <div className="finance-section-heading">
          <span className="finance-step">01</span>
          <div><h2>Veicolo e polizza</h2><p>Scegli una pratica inviata per precompilare i riferimenti.</p></div>
        </div>
        <div className="finance-selection card">
          <div className="form-group finance-select">
            <label htmlFor="finance-policy">Polizza</label>
            {loading ? <div className="loading">Caricamento polizze…</div> : (
            <select value={praticaId} onChange={e => {
              const id = e.target.value
              setPraticaId(id)
              const selezionata = pratiche.find(p => p.id === id)
              if (selezionata) {
                const prev = selezionata.preventivi?.[selezionata.scelta_compagnia]
                const costoCvt = numero(prev?.totale_assicurazione ?? prev?.totale ?? selezionata.totale_finale)
                setImportoFinanziato(String(numero(selezionata.dati_comuni?.valore_auto) + costoCvt))
              }
              else setImportoFinanziato('')
            }} id="finance-policy">
              <option value="">Seleziona una polizza…</option>
              {pratiche.map(p => (
                <option key={p.id} value={p.id}>
                  {p.numero_pratica || p.id.slice(0, 8)} · {p.cliente_cognome} {p.cliente_nome} · {p.dati_comuni?.targa || 'senza targa'}
                </option>
              ))}
            </select>
          )}
            {!loading && !pratiche.length && <div className="hint">Non risultano pratiche inviate associate al tuo utente.</div>}
          </div>

          {pratica ? (
            <div className="finance-reference">
              <div><span>Prezzo auto</span><strong>{formatEuro(prezzoAuto)}</strong></div>
              <div><span>Premio CVT</span><strong>{formatEuro(premioCvt)}</strong></div>
              <div><span>Compagnia</span><strong>{pratica.scelta_compagnia || '—'}</strong></div>
              <div className="finance-reference-total"><span>Auto + CVT</span><strong>{formatEuro(importoRiferimento)}</strong></div>
            </div>
          ) : (
            <div className="finance-empty">Seleziona una polizza per vedere prezzo auto e premio CVT.</div>
          )}
        </div>
      </section>

      <section className="finance-section">
        <div className="finance-section-heading">
          <span className="finance-step">02</span>
          <div><h2>Impostazioni finanziamento</h2><p>L’importo può essere diverso dal totale indicativo di auto e CVT.</p></div>
        </div>
        <div className="card finance-inputs">
          <div className="form-group">
            <label htmlFor="finance-amount">Importo da finanziare</label>
            <div className="finance-input-wrap"><span>€</span><input id="finance-amount" type="number" min="0" step="0.01" value={importoFinanziato} onChange={e => setImportoFinanziato(e.target.value)} placeholder="25.000" /></div>
          </div>
          <div className="form-group">
            <label htmlFor="finance-fees">Costi fissi eventuali</label>
            <div className="finance-input-wrap"><span>€</span><input id="finance-fees" type="number" min="0" step="0.01" value={costiFissi} onChange={e => setCostiFissi(e.target.value)} placeholder="0" /></div>
          </div>
          <div className="form-group">
            <label htmlFor="finance-monthly-fees">Costi Extra Mensili</label>
            <div className="finance-input-wrap"><span>€</span><input id="finance-monthly-fees" type="number" min="0" step="0.01" value={costiExtraMensili} onChange={e => setCostiExtraMensili(e.target.value)} placeholder="0" /></div>
          </div>
          <div className="form-group">
            <label htmlFor="finance-duration">Durata</label>
            <div className="finance-input-wrap"><input id="finance-duration" type="number" min="1" step="1" value={durataMesi} onChange={e => setDurataMesi(e.target.value)} /><span>mesi</span></div>
          </div>
          <div className="form-group">
            <label htmlFor="finance-tan">TAN</label>
            <div className="finance-input-wrap"><input id="finance-tan" type="number" min="0" step="0.01" value={tan} onChange={e => setTan(e.target.value)} placeholder="7,50" /><span>%</span></div>
          </div>
          <div className="form-group">
            <label htmlFor="finance-taeg">TAEG</label>
            <div className="finance-input-wrap"><input id="finance-taeg" type="number" min="0" step="0.01" value={taeg} onChange={e => setTaeg(e.target.value)} placeholder="8,20" /><span>%</span></div>
          </div>
        </div>
        <p className="finance-note">I costi fissi si aggiungono una volta al capitale; i costi extra mensili si sommano a ogni rata. La rata finanziaria è calcolata con il TAN.</p>
      </section>

      <section className="finance-result" aria-live="polite">
        <div className="finance-result-main">
          <div className="finance-result-label">Rata mensile stimata</div>
          <div className="finance-result-value">{pratica && totaleMensile != null ? formatEuro(totaleMensile) : '—'}</div>
          <div className="finance-result-caption">{pratica && durata > 0 ? `totale mensile per ${durata} mesi` : 'Seleziona una polizza e inserisci i parametri'}</div>
        </div>
        <div className="finance-result-details">
          <div><span>Capitale incl. costi fissi</span><strong>{pratica ? formatEuro(capitale) : '—'}</strong></div>
          <div><span>Rata finanziaria (TAN {tanAnnuale.toFixed(2).replace('.', ',')}%)</span><strong>{pratica && rata != null ? formatEuro(rata) : '—'}</strong></div>
          <div><span>Costi extra mensili</span><strong>{pratica ? formatEuro(numero(costiExtraMensili)) : '—'}</strong></div>
          <div><span>Totale pagamenti stimato</span><strong>{pratica && totalePagamenti != null ? formatEuro(totalePagamenti) : '—'}</strong></div>
          <div><span>Interessi stimati</span><strong>{pratica && interessiStimati != null ? formatEuro(interessiStimati) : '—'}</strong></div>
          {taeg !== '' && <div><span>TAEG indicato</span><strong>{numero(taeg).toFixed(2).replace('.', ',')}%</strong></div>}
        </div>
      </section>
    </div>
  )
}
